import OptimizedImage from "@/components/OptimizedImage";
import React, { useState } from "react";
import ArtistVideoPlayer from "../../utils/ArtistVideoPlayer";
import { useGalleryImagesForPreview } from "@/api/hooks/useGalleryImagesForPreview";
import { useGalleryVideosForPreview } from "@/api/hooks/useGalleryVideosForPreview";
import { useArtistProfilePic } from "@/api/hooks/useArtistDisplayName";

const GalleryPlaceholder = ({
  className,
}: {
  className?: string;
}) => (
  <div
    className={`bg-[#2C2C2E] flex items-center justify-center rounded-3xl max-md:rounded ${className}`}
    aria-hidden
  >
    <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-full border-2 border-[#8E8E93] border-dashed opacity-50" />
  </div>
);

const VideoPlaceholder = ({ className }: { className?: string }) => (
  <div
    className={`bg-[#2C2C2E] flex items-center justify-center rounded-lg ${className}`}
    aria-hidden
  >
    <div className="w-12 h-12 rounded-full border-2 border-[#8E8E93] border-dashed opacity-50" />
  </div>
);

const ArtistFreeGallery = () => {
  const [activeTab, setActiveTab] = useState<"photos" | "videos">("photos");
  const { slots } = useGalleryImagesForPreview("free");
  const { slots: videoSlots } = useGalleryVideosForPreview("free");
  const profilePic = useArtistProfilePic();
  return (
    <div className="flex justify-center flex-col items-center  mt-20 w-screen">
      {/* Tab Container */}
      <div className="relative flex bg-none p-3 rounded-full w-54 gap-10 shadow-2xl shadow-black">
        {/* Sliding Background */}
        <div
          className={`absolute top-0 left-0 w-1/2 px-2 h-full bg-white rounded-full shadow-md transition-all duration-300 ${activeTab === "videos" ? "translate-x-full" : "translate-x-0"
            }`}
        />

        {/* Tabs */}
        <p
          className={`w-1/2 ml-3 sm:px-2 text-center text-[20px] sm:text-[30px] sm:py-2 cursor-pointer relative z-10 ${activeTab === "photos" ? "text-black " : "text-gray-600"
            }`}
          onClick={() => setActiveTab("photos")}
        >
          Photos
        </p>
        <p
          className={`w-1/2 mr-3 sm:px-2 text-center text-[20px] sm:text-[30px] sm:py-2 cursor-pointer relative z-10 ${activeTab === "videos" ? "text-black " : "text-gray-600"
            }`}
          onClick={() => setActiveTab("videos")}
        >
          Videos
        </p>
      </div>

      {/* Content Display */}
      <div className="mt-4 w-full">
        {activeTab === "photos" && (
          <div className="grid grid-cols-2 justify-items-center place-content-center gap-20 max-md:gap-4 py-20 max-md:py-10">
            {slots.map((slot, index) => (
              <div
                key={slot.type === "image" ? slot.id : `ph-${slot.index}`}
                className="relative w-full max-w-[700px] h-[500px] max-md:max-w-[180px] max-md:h-[210px] rounded-3xl max-md:rounded pointer-events-none overflow-hidden"
              >
                {slot.type === "image" ? (
                  <OptimizedImage
                    src={slot.url}
                    alt={`Gallery ${index + 1}`}
                    fill={true}
                    className="object-cover object-top rounded-3xl max-md:rounded"
                  />
                ) : (
                  <GalleryPlaceholder className="w-full h-full" />
                )}
              </div>
            ))}
          </div>
        )}
        {activeTab === "videos" && (
          <div className="mt-20 px-4 max-w-2xl mx-auto min-h-[300px]">
            <div className="grid grid-cols-1 gap-4">
              {videoSlots.map((slot) =>
                slot.type === "video" ? (
                  <div
                    key={slot.id}
                    className="relative w-full h-[400px] max-md:h-[300px] border border-zinc-800 rounded-lg overflow-hidden pointer-events-none"
                  >
                    <ArtistVideoPlayer
                      source={slot.source}
                      isThumbnail
                      profile_pic={profilePic ?? ""}
                      containerClassName="relative w-full h-full"
                      imageClassName="object-cover"
                      overlayClassName="absolute top-0 left-0 w-full h-full bg-black/40 flex items-center justify-center"
                      durationClassName="absolute bottom-4 right-4 font-bold text-lg p-4 text-white"
                      titleClassName="absolute top-2 left-2 text-lg p-4 text-white"
                    />
                  </div>
                ) : (
                  <div
                    key={`ph-${slot.index}`}
                    className="relative w-full h-[400px] max-md:h-[300px] border border-zinc-800 rounded-lg overflow-hidden pointer-events-none flex items-center justify-center"
                  >
                    <VideoPlaceholder className="w-full h-full rounded-lg" />
                  </div>
                )
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ArtistFreeGallery;
